import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw
} from 'lucide-react';

const Logs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Données simulées
  const [logs, setLogs] = useState([
    {
      id: 1,
      campaign_id: 1,
      campaign_name: 'Promotion Été 2024',
      contact_name: 'Jean Dupont',
      numero_destination: '+33123456789',
      message_envoye: 'Découvrez nos offres d\'été avec -30% sur tous les produits !',
      statut_envoi: 'envoye',
      date_envoi: '2024-06-15T10:30:00',
      error_message: null
    },
    {
      id: 2,
      campaign_id: 1,
      campaign_name: 'Promotion Été 2024',
      contact_name: 'Marie Martin',
      numero_destination: '+33987654321',
      message_envoye: 'Découvrez nos offres d\'été avec -30% sur tous les produits !',
      statut_envoi: 'envoye',
      date_envoi: '2024-06-15T10:31:00',
      error_message: null
    },
    {
      id: 3,
      campaign_id: 2,
      campaign_name: 'Rappel RDV',
      contact_name: 'Pierre Bernard',
      numero_destination: '+33555666777',
      message_envoye: 'N\'oubliez pas votre rendez-vous demain à 14h.',
      statut_envoi: 'echec',
      date_envoi: '2024-06-16T09:15:00',
      error_message: 'Numéro invalide'
    },
    {
      id: 4,
      campaign_id: 2,
      campaign_name: 'Rappel RDV',
      contact_name: 'Sophie Durand',
      numero_destination: '+33111222333',
      message_envoye: 'N\'oubliez pas votre rendez-vous demain à 14h.',
      statut_envoi: 'en_attente',
      date_envoi: '2024-06-16T09:16:00',
      error_message: null
    },
  ]);

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.numero_destination.includes(searchTerm) ||
      log.message_envoye.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.campaign_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || log.statut_envoi === statusFilter;
    
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const logDate = new Date(log.date_envoi);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      switch (dateFilter) {
        case 'today':
          matchesDate = logDate >= today;
          break;
        case 'yesterday':
          matchesDate = logDate >= yesterday && logDate < today;
          break;
        case 'week':
          matchesDate = logDate >= weekAgo;
          break;
        default:
          matchesDate = true;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'envoye':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'echec':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'en_attente':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'envoye':
        return 'text-green-600 bg-green-100';
      case 'echec':
        return 'text-red-600 bg-red-100';
      case 'en_attente':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'envoye':
        return 'Envoyé';
      case 'echec':
        return 'Échec';
      case 'en_attente':
        return 'En attente';
      default:
        return status;
    }
  };

  const stats = {
    total: logs.length,
    envoyes: logs.filter(l => l.statut_envoi === 'envoye').length,
    echecs: logs.filter(l => l.statut_envoi === 'echec').length,
    en_attente: logs.filter(l => l.statut_envoi === 'en_attente').length,
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Logs SMS</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Historique détaillé des envois de SMS
          </p>
        </div>
        <div className="flex space-x-3">
          <button className="btn btn-secondary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
          <button className="btn btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                <span className="text-sm font-medium text-blue-600">{stats.total}</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-secondary-500 truncate">
                  Total SMS
                </dt>
                <dd className="text-lg font-medium text-secondary-900">
                  {stats.total}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                <span className="text-sm font-medium text-green-600">{stats.envoyes}</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-secondary-500 truncate">
                  Envoyés
                </dt>
                <dd className="text-lg font-medium text-secondary-900">
                  {stats.envoyes}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
                <span className="text-sm font-medium text-red-600">{stats.echecs}</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-secondary-500 truncate">
                  Échecs
                </dt>
                <dd className="text-lg font-medium text-secondary-900">
                  {stats.echecs}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100">
                <span className="text-sm font-medium text-yellow-600">{stats.en_attente}</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-secondary-500 truncate">
                  En attente
                </dt>
                <dd className="text-lg font-medium text-secondary-900">
                  {stats.en_attente}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Rechercher dans les logs..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="envoye">Envoyé</option>
              <option value="echec">Échec</option>
              <option value="en_attente">En attente</option>
            </select>
            <select
              className="input"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="yesterday">Hier</option>
              <option value="week">Cette semaine</option>
            </select>
            <button className="btn btn-secondary">
              <Filter className="h-4 w-4 mr-2" />
              Filtres
            </button>
          </div>
        </div>
      </div>

      {/* Tableau des logs */}
      <div className="card">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="table">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Campagne</th>
                <th>Contact</th>
                <th>Numéro</th>
                <th>Message</th>
                <th>Date d'envoi</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id}>
                  <td>
                    <div className="flex items-center">
                      {getStatusIcon(log.statut_envoi)}
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.statut_envoi)}`}>
                        {getStatusLabel(log.statut_envoi)}
                      </span>
                    </div>
                  </td>
                  <td className="font-medium text-secondary-900">
                    {log.campaign_name}
                  </td>
                  <td>{log.contact_name}</td>
                  <td>{log.numero_destination}</td>
                  <td className="max-w-xs truncate" title={log.message_envoye}>
                    {log.message_envoye}
                  </td>
                  <td>{new Date(log.date_envoi).toLocaleString('fr-FR')}</td>
                  <td>
                    <div className="flex space-x-2">
                      <button className="text-primary-600 hover:text-primary-900">
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredLogs.length === 0 && (
        <div className="text-center py-12">
          <Clock className="mx-auto h-12 w-12 text-secondary-400" />
          <h3 className="mt-2 text-sm font-medium text-secondary-900">Aucun log trouvé</h3>
          <p className="mt-1 text-sm text-secondary-500">
            Aucun SMS n'a encore été envoyé ou ne correspond à vos critères de recherche.
          </p>
        </div>
      )}
    </div>
  );
};

export default Logs;
